import React from 'react';
import { isNil } from 'lodash';

import CardDialog from '~/components/CardDialog';
import ClaimLink from '~/components/ClaimLink';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, PropertyDetails, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import TEXTS from '../texts';
import type { SorIssue, SorResolveAttemptParams, SorUser } from '../types';
import { SorCommitStatus, SorIssueStatus } from '../types';

import SorIssueStatusChip from './SorIssueStatusChip';
import SorResolveForm from './SorResolveForm';

interface SorIssueDetailsDialogProps {
  issue: SorIssue;
  replayUsers: SorUser[];
  userName: (userId: string) => string;
  onResolve: (params: SorResolveAttemptParams) => Promise<void>;
  onClose: () => void;
}

const toLocalDateTime = (serverDateTime: string | null | undefined) =>
  isNil(serverDateTime) ? null : serverDateTimeToLocal(serverDateTime);

const JsonSection: React.FC<{ title: string; value: SorIssue['sent_payload']; emptyText: string }> = ({
  title,
  value,
  emptyText,
}) => (
  <CollapsibleWrapper title={title}>
    {isNil(value) ? (
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
        {emptyText}
      </Text>
    ) : (
      <pre className="font-mono max-h-60 m-0 overflow-auto rounded-xl border border-slate-300 bg-slate-100 p-16 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    )}
  </CollapsibleWrapper>
);

const SorIssueDetailsDialog: React.FC<SorIssueDetailsDialogProps> = ({
  issue,
  replayUsers,
  userName,
  onResolve,
  onClose,
}) => {
  const canResolve = useHasPermission({ action: PERMISSION_ACTIONS.SOR_ISSUES_MANAGE, verb: PERMISSION_VERBS.WRITE });

  return (
    <CardDialog
      isDialog
      title={TEXTS.issueDetails.title(issue.id)}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      footerActions={
        <Button variant="contained" color="primary" onClick={onClose}>
          {TEXTS.issueDetails.close}
        </Button>
      }
    >
      <div className="flex flex-col gap-20">
        <div className="grid grid-cols-3 gap-20">
          <PropertyDetails
            title={TEXTS.issueDetails.status}
            text={null}
            textComponent={<SorIssueStatusChip status={issue.issue_status} />}
          />
          <PropertyDetails title={TEXTS.issueDetails.commitStatus} text={TEXTS.issueStatuses[issue.status]} />
          <PropertyDetails title={TEXTS.issueDetails.operation} text={issue.label} />
          <PropertyDetails
            title={TEXTS.issueDetails.entity}
            text={TEXTS.issueDetails.entityValue(issue.entity_type, issue.entity_id)}
          />
          <PropertyDetails title={TEXTS.issueDetails.apiIdentifier} text={issue.api_identifier} />
          <PropertyDetails
            title={TEXTS.issueDetails.claim}
            text={null}
            textComponent={<ClaimLink claimId={issue.claim_id} linkText={issue.claim_number} />}
          />
          <PropertyDetails title={TEXTS.issueDetails.requestedBy} text={userName(issue.requested_by_user_id)} />
          {!isNil(issue.resolved_by_user_id) && (
            <PropertyDetails title={TEXTS.issueDetails.resolvedBy} text={userName(issue.resolved_by_user_id)} />
          )}
          {!isNil(issue.replayed_as_user_id) && (
            <PropertyDetails title={TEXTS.issueDetails.replayedAs} text={userName(issue.replayed_as_user_id)} />
          )}
          <PropertyDetails title={TEXTS.issueDetails.idempotencyKey} text={issue.idempotency_key} />
          <PropertyDetails title={TEXTS.issueDetails.sorReference} text={issue.sor_external_id} />
          <PropertyDetails title={TEXTS.issueDetails.started} text={toLocalDateTime(issue.started_at)} />
          <PropertyDetails title={TEXTS.issueDetails.sent} text={toLocalDateTime(issue.sent_at)} />
          <PropertyDetails title={TEXTS.issueDetails.responded} text={toLocalDateTime(issue.responded_at)} />
          <PropertyDetails
            title={TEXTS.issueDetails.sorLatency}
            text={isNil(issue.sor_latency_ms) ? null : TEXTS.latency(issue.sor_latency_ms)}
          />
          {!isNil(issue.resolution_note) && (
            <div className="col-span-3">
              <PropertyDetails title={TEXTS.issueDetails.resolutionNote} text={issue.resolution_note} />
            </div>
          )}
          <div className="col-span-3">
            <PropertyDetails
              title={
                issue.status === SorCommitStatus.Completed
                  ? TEXTS.issueDetails.errorBeforeConfirmed
                  : TEXTS.issueDetails.error
              }
              text={issue.error}
            />
          </div>
        </div>
        <JsonSection
          title={TEXTS.issueDetails.sentPayload}
          value={issue.sent_payload}
          emptyText={TEXTS.issueDetails.notSent}
        />
        <JsonSection
          title={TEXTS.issueDetails.sorResponse}
          value={issue.sor_response}
          emptyText={TEXTS.issueDetails.noSorResponse}
        />
        <CollapsibleWrapper title={TEXTS.issueDetails.reconciliationHistory}>
          <div className="grid grid-cols-3 gap-20">
            <PropertyDetails
              title={TEXTS.issueDetails.tries}
              text={TEXTS.issues.triesValue(issue.tries, issue.max_tries)}
            />
            <PropertyDetails
              title={TEXTS.issueDetails.lastReconciled}
              text={toLocalDateTime(issue.last_reconciled_at)}
            />
          </div>
        </CollapsibleWrapper>
        {canResolve && issue.issue_status === SorIssueStatus.NeedsAttention && (
          <SorResolveForm issue={issue} replayUsers={replayUsers} userName={userName} onResolve={onResolve} />
        )}
      </div>
    </CardDialog>
  );
};

export default SorIssueDetailsDialog;
